import React from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProductCard({ product }) {
  const { token } = useAuth();
  const { refreshCart } = useCart();

  const handleAddToCart = async (e) => {
    e.preventDefault();
    
    if (!token) {
      toast.error("Please login to add items to cart");
      return;
    }

    try {
      await axios.post(
        `${API}/cart/add`,
        { product_id: product.id, quantity: 1 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshCart();
      toast.success("Added to cart!");
    } catch (error) {
      toast.error("Failed to add to cart");
    }
  };

  return (
    <Link to={`/products/${product.id}`} data-testid="product-card">
      <div className="bg-card rounded-2xl overflow-hidden shadow-soft hover:shadow-hover transition-all duration-300 hover:-translate-y-1">
        <div className="aspect-square overflow-hidden bg-muted">
          <img
            src={product.images?.[0] || "https://images.unsplash.com/photo-1564682628523-316b685c5d7b?w=400"}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
            data-testid="product-card-image"
          />
        </div>
        <div className="p-6">
          <h3 className="font-heading text-xl font-semibold mb-2 text-foreground" data-testid="product-card-name">
            {product.name}
          </h3>
          <p className="text-muted-foreground text-sm mb-2 line-clamp-2">
            {product.description}
          </p>
          {product.vendor_location && (
            <div className="flex items-center text-xs text-muted-foreground mb-3">
              <MapPin className="w-3 h-3 mr-1" />
              <span>{product.vendor_location.city}, {product.vendor_location.district}</span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              {product.discount_percentage > 0 ? (
                <>
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-sm text-muted-foreground line-through">₹{product.actual_price}</span>
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full font-semibold">
                      {product.discount_percentage}% OFF
                    </span>
                  </div>
                  <span className="font-heading text-2xl font-bold text-primary" data-testid="product-card-price">
                    ₹{product.offer_price}
                  </span>
                </>
              ) : (
                <span className="font-heading text-2xl font-bold text-primary" data-testid="product-card-price">
                  ₹{product.offer_price || product.price}
                </span>
              )}
            </div>
            <Button
              onClick={handleAddToCart}
              className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-4 py-2"
              data-testid="product-card-add-to-cart-btn"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Add
            </Button>
          </div>
        </div>
      </div>
    </Link>
  );
}
